<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Agent Privilege' : 'Create Agent Privilege' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Link &amp; Scope</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6">
            <label>INSURANCE PARTNER NAME <span class="required">*</span></label>
            <v-select
              dense
              outlined
              hide-details
              :items="insurancePartnerOptions"
              item-text="text"
              item-value="value"
              v-model="form.insurance_partner_id"
            />
          </v-col>
          <v-col v-if="isSuperAdmin" cols="12" md="6">
            <label>BRANCH <span class="required">*</span></label>
            <v-select
              dense
              outlined
              hide-details
              :items="orgOptions"
              item-text="text"
              item-value="value"
              v-model="form.org_codename"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Campaign Details</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>CAMPAIGN TITLE <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_th" placeholder="เช่น ส่วนลดตรวจสุขภาพ 20%"/></v-col>
          <v-col cols="12" md="6"><label>CAMPAIGN TITLE <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_en" placeholder="e.g. Health Check Discount 20%"/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียดแคมเปญ..."/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Campaign description..."/></v-col>
          <v-col cols="12" md="6">
            <label>VALID FROM <span class="required">*</span></label>
            <v-menu
              v-model="validFromMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.valid_from"
                  dense
                  outlined
                  hide-details
                  readonly
                  placeholder="YYYY-MM-DD"
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="form.valid_from" @input="validFromMenu = false" />
            </v-menu>
          </v-col>

          <v-col cols="12" md="6">
            <label>VALID TO <span class="required">*</span></label>
            <v-menu
              v-model="validToMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.valid_to"
                  dense
                  outlined
                  hide-details
                  readonly
                  placeholder="YYYY-MM-DD"
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.valid_to"
                :min="form.valid_from || undefined"
                @input="validToMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save Privilege</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  org_codename: '',
  insurance_partner_id: '',
  title_th: '',
  title_en: '',
  description_th: '',
  description_en: '',
  valid_from: '',
  valid_to: ''
})

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return { form: defaultForm(), initialLoading: false, validFromMenu: false, validToMenu: false }
  },
  computed: {
    ...mapState('agentPrivileges', ['list', 'isSaving', 'orgOptions', 'insurancePartnerOptions']),
    ...mapState('auth', ['user']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    canSave() { return this.isEditMode ? hasPermision('CanEditAgentPrivilege') : hasPermision('CanAddAgentPrivilege') },
    isDateRangeValid() {
      if (!this.form.valid_from || !this.form.valid_to) return false
      const from = new Date(this.form.valid_from).getTime()
      const to = new Date(this.form.valid_to).getTime()
      return Number.isFinite(from) && Number.isFinite(to) && to >= from
    },
    isFormValid() {
      return !!(
        this.form.org_codename &&
        this.form.insurance_partner_id &&
        this.form.title_th &&
        this.form.title_en &&
        this.isDateRangeValid
      )
    }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('agent-privileges')
      await this.fetchOrgOptions()
      await this.fetchInsurancePartnerOptions()
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          this.form = {
            ...defaultForm(),
            ...target,
            valid_from: this.normalizeDate(target.valid_from),
            valid_to: this.normalizeDate(target.valid_to)
          }
          if (!this.form.org_codename) {
            this.form.org_codename = this.getUserOrgCode() || (this.orgOptions[0] ? this.orgOptions[0].value : '')
          }
        }
      } else {
        this.form.org_codename = this.getUserOrgCode() || (this.orgOptions[0] ? this.orgOptions[0].value : '')
        this.form.insurance_partner_id = this.insurancePartnerOptions[0] ? this.insurancePartnerOptions[0].value : ''
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('agentPrivileges', ['setResource', 'fetch', 'save', 'fetchOrgOptions', 'fetchInsurancePartnerOptions']),
    normalizeDate(value) {
      if (!value) return ''
      const stringValue = String(value)
      if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) return stringValue
      const date = new Date(stringValue)
      if (Number.isNaN(date.getTime())) return ''
      return date.toISOString().slice(0, 10)
    },
    getUserOrgCode() {
      const currentUser = this.user || {}
      return currentUser.org_code || currentUser.org_codename || currentUser.org || ''
    },
    onCancel() { this.$router.push({name: 'agent-privilege-list'}) },
    async onSave() {
      const result = await this.$store.dispatch('agentPrivileges/save', this.form)
      if (!result.ok) return
      this.$router.push({name: 'agent-privilege-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #64748b; font-size: 13px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e6e9f0; border-radius: 10px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 16px; font-weight: 700; color: #0f1f3b; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.required { color: #c62828; }
.lang { color: #2962ff; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
.actions-card { margin-bottom: 0; }
</style>
